import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Home "Explorar por categoría" grid. 4-column compact layout with
 * real market-model car photos (via Unsplash) instead of emoji. Each
 * image ships with a lazy-load hint + emoji fallback if the URL fails.
 * Photos picked to reflect CURRENT Mexico market inventory:
 *   • Eléctricos → BYD-style compact EV
 *   • Lujo → Mercedes / Range Rover class
 *   • Pickups → Ford F-150 class
 *   • Deportivos → Porsche 911 class
 *   • ...etc
 */

const CATEGORIES = [
  {
    key: "suv", label: "SUVs", emoji: "🚙",
    from: "#1E293B", to: "#1E3A8A",
    photo: "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=400&q=70&auto=format&fit=crop",
  },
  {
    key: "luxury", label: "Lujo", emoji: "💎",
    from: "#1E1B4B", to: "#312E81",
    photo: "https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=400&q=70&auto=format&fit=crop",
  },
  {
    key: "truck", label: "Pickups", emoji: "🛻",
    from: "#1F2937", to: "#374151",
    photo: "https://images.unsplash.com/photo-1605893477799-b99e3b8b93fe?w=400&q=70&auto=format&fit=crop",
  },
  {
    key: "electric", label: "Eléctricos", emoji: "⚡",
    from: "#022C22", to: "#065F46",
    photo: "https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=400&q=70&auto=format&fit=crop",
  },
  {
    key: "compact", label: "Compactos", emoji: "🚗",
    from: "#1F2937", to: "#334155",
    photo: "https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=400&q=70&auto=format&fit=crop",
  },
  {
    key: "sports", label: "Deportivos", emoji: "🏎",
    from: "#1E1B4B", to: "#7C2D12",
    photo: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=400&q=70&auto=format&fit=crop",
  },
  {
    key: "sedan", label: "Sedanes", emoji: "🚘",
    from: "#111827", to: "#334155",
    photo: "https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=400&q=70&auto=format&fit=crop",
  },
  {
    key: "van", label: "Vans", emoji: "🚐",
    from: "#0F172A", to: "#1E3A8A",
    photo: "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=400&q=70&auto=format&fit=crop",
  },
];

// Backend has no `sedan` bucket; alias to `economy`.
const SEARCH_KEY_MAP = { sedan: "economy" };

function CategoryTile({ c }) {
  const { t } = useTranslation();
  const label = t(`categoryGrid.${c.key}`, c.label);
  const [imgOk, setImgOk] = useState(true);
  const backendKey = SEARCH_KEY_MAP[c.key] || c.key;
  return (
    <Link
      to={`/search?category=${backendKey}&city=Tijuana`}
      className="group flex flex-col items-center"
      data-testid={`category-card-${c.key}`}
      aria-label={label}
    >
      <div
        className="relative w-full aspect-square rounded-2xl overflow-hidden shadow-md group-hover:shadow-xl group-hover:-translate-y-0.5 group-active:translate-y-0 group-active:scale-95 transition-all duration-200 flex items-center justify-center"
        style={{ background: `linear-gradient(160deg, ${c.from} 0%, ${c.to} 100%)` }}
      >
        <span
          className="absolute -top-4 -left-4 w-16 h-16 rounded-full bg-white/10 blur-xl"
          aria-hidden
        />
        {imgOk ? (
          <img
            src={c.photo}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setImgOk(false)}
            className="relative z-10 w-4/5 h-4/5 object-contain drop-shadow-[0_10px_20px_rgba(0,0,0,0.55)] group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <span className="text-3xl md:text-4xl drop-shadow-lg" aria-hidden>{c.emoji}</span>
        )}
        <span className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10 pointer-events-none" aria-hidden />
      </div>
      <p className="mt-2 text-xs md:text-sm font-medium text-agave dark:text-neutral-200 leading-tight text-center">
        {label}
      </p>
    </Link>
  );
}

export default function CategoryGrid() {
  const { t } = useTranslation();
  return (
    <section className="py-6 md:py-8 bg-sand dark:bg-[#0F0F12]" data-testid="home-category-grid">
      <div className="container-main">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-agave dark:text-white">
            {t("categoryGrid.title")}
          </h2>
          <Link
            to="/search"
            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline underline-offset-2"
            data-testid="category-see-all"
          >
            {t("categoryGrid.seeAll")}
          </Link>
        </div>

        <div className="grid grid-cols-4 gap-2.5 md:gap-3">
          {CATEGORIES.map((c) => <CategoryTile key={c.key} c={c} />)}
        </div>
      </div>
    </section>
  );
}
